import { useState, useEffect } from "react";
import { api } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle, DrawerTrigger, DrawerFooter, DrawerClose } from "@/components/ui/drawer";
import { Textarea } from "@/components/ui/textarea";
import { DatePicker } from "@/components/ui/date-picker";
import { CompoundSelect } from "@/components/ui/compound-select";
import { toast } from "sonner";
import { format, parseISO } from "date-fns";
import { Plus, FlaskConical, Trash2, Loader2, Calendar, AlertTriangle } from "lucide-react";
import AppLogo from "@/components/AppLogo";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

export default function VialsPage() {
  const [vials, setVials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [vialToDelete, setVialToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [form, setForm] = useState({
    compound_name: "",
    vial_size: "",
    reconstitution_date: format(new Date(), "yyyy-MM-dd"),
    notes: "",
  });

  const fetchVials = async () => {
    try {
      const res = await api.get("/vials");
      setVials(res.data);
    } catch (error) {
      toast.error("Failed to load vials");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchVials();
  }, []);

  const resetForm = () => {
    setForm({
      compound_name: "",
      vial_size: "",
      reconstitution_date: format(new Date(), "yyyy-MM-dd"),
      notes: "",
    });
  };

  const handleSubmit = async () => {
    if (!form.compound_name || !form.vial_size) {
      toast.error("Please fill in required fields");
      return;
    }

    setSubmitting(true);
    try {
      await api.post("/vials", form);
      toast.success("Vial logged");
      setDrawerOpen(false);
      resetForm();
      fetchVials();
    } catch (error) {
      toast.error("Failed to log vial");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!vialToDelete) return;
    
    setDeleting(true);
    try {
      await api.delete(`/vials/${vialToDelete.id}`);
      toast.success("Vial deleted");
      setDeleteDialogOpen(false);
      setVialToDelete(null);
      fetchVials();
    } catch (error) {
      toast.error("Failed to delete vial");
    } finally {
      setDeleting(false);
    }
  };

  const openDeleteDialog = (vial) => {
    setVialToDelete(vial);
    setDeleteDialogOpen(true);
  };

  const getDaysBadgeClass = (days) => {
    if (days <= 14) return "fresh";
    if (days <= 28) return "warning";
    return "old";
  };

  return (
    <div className="page-container" data-testid="vials-page">
      {/* Header */}
      <header className="sticky top-0 z-40 glass border-b border-border/50">
        <div className="flex items-center justify-between p-4">
          <div className="flex items-center gap-3">
            <AppLogo size="small" showText={false} />
            <h1 className="font-heading text-lg font-bold">Vial Tracker</h1>
          </div>
          <Drawer open={drawerOpen} onOpenChange={(open) => {
            setDrawerOpen(open);
            if (!open) resetForm();
          }}>
            <DrawerTrigger asChild>
              <Button className="rounded-full" data-testid="add-vial-btn">
                <Plus size={18} className="mr-2" />
                Log Vial
              </Button>
            </DrawerTrigger>
            <DrawerContent>
              <DrawerHeader>
                <DrawerTitle className="font-heading">Log Reconstituted Vial</DrawerTitle>
              </DrawerHeader>
              
              <div className="px-4 pb-4 space-y-4">
                {/* Compound Name */}
                <div className="space-y-2">
                  <Label htmlFor="compound_name">Compound Name *</Label>
                  <CompoundSelect
                    value={form.compound_name}
                    onChange={(value) => setForm(prev => ({ ...prev, compound_name: value }))}
                    placeholder="Select or type compound..."
                    data-testid="vial-compound-input"
                  />
                </div>

                {/* Vial Size */}
                <div className="space-y-2">
                  <Label htmlFor="vial_size">Vial Size *</Label>
                  <Input
                    id="vial_size"
                    placeholder="e.g., 5mg"
                    value={form.vial_size}
                    onChange={(e) => setForm(prev => ({ ...prev, vial_size: e.target.value }))}
                    className="h-12"
                    data-testid="vial-size-input"
                  />
                </div>

                {/* Reconstitution Date */}
                <div className="space-y-2">
                  <Label htmlFor="reconstitution_date">Reconstitution Date</Label>
                  <DatePicker
                    value={form.reconstitution_date}
                    onChange={(date) => setForm(prev => ({ ...prev, reconstitution_date: date }))}
                    data-testid="vial-date-input"
                  />
                </div>

                {/* Notes */}
                <div className="space-y-2">
                  <Label htmlFor="notes">Notes (optional)</Label>
                  <Textarea
                    id="notes"
                    placeholder="Storage location, batch number, etc."
                    value={form.notes}
                    onChange={(e) => setForm(prev => ({ ...prev, notes: e.target.value }))}
                    className="min-h-[80px]"
                    data-testid="vial-notes-input"
                  />
                </div>
              </div>

              <DrawerFooter className="pt-2">
                <Button 
                  onClick={handleSubmit} 
                  disabled={submitting}
                  className="h-12 rounded-full"
                  data-testid="vial-save-btn"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    "Log Vial"
                  )}
                </Button>
                <DrawerClose asChild>
                  <Button variant="outline" className="h-12 rounded-full">Cancel</Button>
                </DrawerClose>
              </DrawerFooter>
            </DrawerContent>
          </Drawer>
        </div>
      </header>

      {/* Content */}
      <main className="p-4 space-y-3">
        {/* Info Note */}
        <div className="bg-muted/50 rounded-lg p-3 flex items-start gap-2 text-sm">
          <FlaskConical size={16} className="text-muted-foreground mt-0.5 flex-shrink-0" />
          <p className="text-muted-foreground">
            Vials are not linked to protocols. Use this to track reconstitution dates and notes only.
          </p>
        </div>

        {loading ? (
          <div className="space-y-3">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-24 rounded-xl bg-muted animate-pulse" />
            ))}
          </div>
        ) : vials.length > 0 ? (
          vials.map((vial, index) => (
            <Card 
              key={vial.id} 
              className={`vial-card animate-fadeIn stagger-${Math.min(index + 1, 5)}`}
              style={{ opacity: 0 }}
              data-testid={`vial-card-${index}`}
            >
              <CardContent className="p-4">
                <div className={`vial-days-badge ${getDaysBadgeClass(vial.days_since_reconstitution)}`}>
                  {vial.days_since_reconstitution} days
                </div>

                <h3 className="font-semibold text-lg pr-16">{vial.compound_name}</h3>
                <p className="text-sm text-muted-foreground mb-2">{vial.vial_size}</p>
                
                <div className="flex items-center gap-1 text-sm text-muted-foreground mb-2">
                  <Calendar size={14} />
                  Reconstituted {format(parseISO(vial.reconstitution_date), "MMM d, yyyy")}
                </div>

                {/* 21-day Warning */}
                {vial.days_since_reconstitution > 21 && (
                  <div className="flex items-center gap-2 mb-2 p-2 bg-destructive/10 rounded-lg border border-destructive/20">
                    <AlertTriangle size={16} className="text-destructive flex-shrink-0" />
                    <span className="text-sm font-medium text-destructive">
                      ⚠ Vial older than 21 days
                    </span>
                  </div>
                )}

                {vial.notes && (
                  <p className="text-sm text-muted-foreground italic mb-3">
                    {vial.notes}
                  </p>
                )}

                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => openDeleteDialog(vial)}
                  className="h-9 px-3 rounded-full text-destructive hover:text-destructive"
                  data-testid={`vial-delete-${index}`}
                >
                  <Trash2 size={16} />
                  <span className="ml-1">Remove</span>
                </Button>
              </CardContent>
            </Card>
          ))
        ) : (
          <Card className="animate-fadeIn">
            <CardContent className="empty-state">
              <FlaskConical className="empty-state-icon" strokeWidth={1} />
              <h3 className="empty-state-title">No vials yet</h3>
              <p className="empty-state-description">
                Add one to track reconstitution dates.
              </p>
            </CardContent>
          </Card>
        )}

        {/* Info Card */}
        {vials.length > 0 && (
          <Card className="animate-fadeIn bg-muted/50" style={{ animationDelay: "0.3s", opacity: 0 }}>
            <CardContent className="p-4">
              <h4 className="font-semibold text-sm mb-2">Storage Guidelines</h4>
              <ul className="text-sm text-muted-foreground space-y-1">
                <li className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-green-500"></span>
                  Green (0-14 days): Optimal potency
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-yellow-500"></span>
                  Yellow (15-28 days): Still usable
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-red-500"></span>
                  Red (28+ days): Consider replacing
                </li>
              </ul>
            </CardContent>
          </Card>
        )}
      </main>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-destructive" />
              Delete Vial?
            </AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete the <strong>{vialToDelete?.compound_name}</strong> vial record. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={deleting}
              className="bg-destructive hover:bg-destructive/90"
            >
              {deleting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Deleting...
                </>
              ) : (
                "Delete Vial"
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
